/**
 * Hand-rolled SSE consumer for the agent's streaming reply.
 *
 * The generated SDK is request/response only — `@hey-api/client-fetch` has no streaming story —
 * so this is the one place the frontend calls `fetch` directly instead of going through the
 * generated client. Same relative `/api/*` URL, same-origin cookie auth; everything else about
 * the API layer is unaffected.
 *
 * Framing assumed (confirm against the backend's SSE writer): one JSON-encoded
 * `AgentStreamEvent` member per `data: <json>\n\n` frame — no multi-line `data:` continuations,
 * no `event:` field (the event's own `type` carries the discriminator), no comment lines.
 */

import type {
  DoneEvent,
  ErrorEvent,
  NarrationDeltaEvent,
  NarrationEvent,
  NarrationRecord,
  StatusEvent,
  TextDeltaEvent,
  ToolCallEvent,
  ToolCallRecord,
  ViewEvent,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Thrown when the POST itself fails (before any SSE frame arrives) — carries the HTTP status
 *  so callers can tell "agent not deployed yet" (501/503) apart from a generic network error. */
export class AgentStreamError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "AgentStreamError";
    this.status = status;
  }
}

export interface StreamAgentMessageHandlers {
  onTextDelta?: (text: string) => void;
  onStatus?: (text: string) => void;
  onView?: (view: ViewEvent["view"]) => void;
  onToolCall?: (toolCall: ToolCallRecord) => void;
  /** A chunk of a narration as the agent writes it: append it to the narration with this `id`,
   *  starting a new one when the id is unseen. Always followed by `onNarration` for the same
   *  id once the call completes. */
  onNarrationDelta?: (id: string, text: string) => void;
  /** The agent's own words about what it's doing mid-turn — always shown, never behind the
   *  tool-call toggle. Authoritative: it REPLACES whatever the deltas for this id built up
   *  (the text is normalised and length-capped server-side). */
  onNarration?: (narration: NarrationRecord) => void;
  onDone?: (payload: DoneEvent) => void;
  onError?: (message: string) => void;
}

/**
 * POSTs the user's message and dispatches each parsed event to the matching handler. Resolves
 * once the stream ends. Throws if the request itself fails (non-2xx, or no body to read) —
 * callers should catch that and treat it the same as a mid-stream `error` event.
 */
export async function streamAgentMessage(
  conversationId: string,
  content: string,
  handlers: StreamAgentMessageHandlers,
  signal?: AbortSignal,
): Promise<void> {
  logger.debug("[agent-stream] send", conversationId);
  const response = await fetch(`/api/agent/conversations/${conversationId}/messages`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
    signal,
  });

  if (!response.ok || !response.body) {
    const detail = await response.text().catch(() => "");
    throw new AgentStreamError(
      response.status,
      `agent stream failed (${response.status})${detail ? `: ${detail}` : ""}`,
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";
    for (const frame of frames) {
      for (const line of frame.split("\n")) {
        if (!line.startsWith("data:")) {
          continue;
        }
        const payload = line.slice(5).trim();
        if (payload) {
          dispatch(payload, handlers);
        }
      }
    }
  }
}

function dispatch(payload: string, handlers: StreamAgentMessageHandlers): void {
  let event: { type?: string };
  try {
    event = JSON.parse(payload);
  } catch (err) {
    logger.warn("[agent-stream] malformed event payload", err);
    return;
  }
  switch (event.type) {
    case "text_delta":
      handlers.onTextDelta?.((event as TextDeltaEvent).text);
      break;
    case "status":
      handlers.onStatus?.((event as StatusEvent).text);
      break;
    case "view":
      handlers.onView?.((event as ViewEvent).view);
      break;
    case "tool_call":
      handlers.onToolCall?.((event as ToolCallEvent).tool_call);
      break;
    case "narration_delta": {
      const delta = event as NarrationDeltaEvent;
      handlers.onNarrationDelta?.(delta.id, delta.text);
      break;
    }
    case "narration":
      handlers.onNarration?.((event as NarrationEvent).narration);
      break;
    case "done":
      handlers.onDone?.(event as DoneEvent);
      break;
    case "error":
      handlers.onError?.((event as ErrorEvent).message);
      break;
    default:
      logger.warn("[agent-stream] unknown event type", event.type);
  }
}
